import { Link } from 'react-router-dom';

import { brand } from '../../config/brand';
import { legal } from '../../config/legal';
import { Clause, LegalPage, Points } from './LegalPage';
import { useDocumentTitle } from '../../lib/title';

/**
 * What uploading music here does and does not give away.
 *
 * ## Why this is a page and not a longer clause 3
 *
 * The terms already say your music stays yours. Nobody reads the terms. The one
 * question every artist has before uploading anything anywhere is "what are you
 * taking", and answering it in clause three of a thirteen-clause document is
 * answering it to the people who were never worried.
 *
 * So this is the same agreement written for the person it matters to, at an
 * address that can be sent to somebody: "here is exactly what you are agreeing
 * to." It grants nothing the terms do not already grant and takes nothing away
 * - if the two ever disagree, the terms are the contract and this says so.
 *
 * ## Every claim is checked against what the code does
 *
 * Written by reading the thing rather than by reaching for the usual paragraph.
 * Broadcasts genuinely are not kept, because `recordings-prune.php` deletes the
 * recording Cloudflare makes as a by-product of being watchable. Auto-refill
 * genuinely can put a public track into a room whose host has never met the
 * artist, so the page says so rather than describing a submission-only world
 * that would read better and be false.
 *
 * A promise in a legal document that the product does not keep is worse than no
 * promise at all, and it is the sort of thing that is only ever discovered by
 * the person it was made to.
 */
export function ArtistRightsPage() {
  useDocumentTitle('Your music and your rights');

  return (
    <LegalPage
      title="Your music and your rights"
      summary={
        `You own your music. Uploading it here does not change that, and nothing on this page ` +
        `transfers a right to us. What it does is give ${brand.name} the specific permissions it ` +
        `needs to play your record: store it, convert it into streaming formats, and let listeners ` +
        `and hosts play it. Nothing beyond that, and only for as long as you keep it here.`
      }
    >
      <Clause id="you-keep-everything" title="1. You keep everything">
        <p>
          You keep every right you had in your recording, your composition, your artwork and your
          name. We claim no ownership of any of it, and uploading transfers nothing.
        </p>
        <Points>
          <li>Nothing here is exclusive. Release the same track anywhere else, on any terms.</li>
          <li>
            We take no share of your publishing, no share of your masters, and no interest in
            anything you do outside this service.
          </li>
          <li>
            We do not license your music to anybody else, place it in compilations, sync it to
            video, or sell it. It is not ours to sell.
          </li>
          <li>We do not use your music to train machine-learning models, ours or anyone else's.</li>
        </Points>
      </Clause>

      <Clause id="permission" title="2. What you are actually giving permission for">
        <p>
          A streaming service cannot play a file without doing several technical things to it. You
          grant us a non-exclusive, worldwide, royalty-free license to do those things and no
          others:
        </p>
        <Points>
          <li>Store your upload, and keep the backups that stop it disappearing.</li>
          <li>
            Convert it into other formats and bitrates so it plays on a phone, a laptop and a slow
            connection.
          </li>
          <li>
            Generate the things the interface needs from it: waveform data, artwork in several
            sizes, the loudness curve the visuals move to.
          </li>
          <li>
            Transmit it to the people you have made it available to, and show its title, artist
            name and artwork wherever it appears on the service.
          </li>
        </Points>
        <p>
          That license exists to operate the service. It is not a right we hold in your work, it
          lasts only as long as you keep the track here, and it ends when you delete it.
        </p>
      </Clause>

      <Clause id="hosts" title="3. Hosts playing your music">
        <p>
          This is what the service is for, so it is worth being exact about. A host runs a live
          room and plays records in it. When your track is public, that includes yours.
        </p>
        <Points>
          <li>
            You can submit a track to any host whose door is open, and so can anybody else who can
            play it.
          </li>
          <li>
            A host can add a public track to their own queue directly, without it being submitted.
          </li>
          <li>
            A room that runs out of music can top itself up from across the platform, which
            deliberately favors artists nobody has heard yet. That means your track can be played
            by a host who has never met you, in a room you have never seen.
          </li>
          <li>
            While a room plays your track, everybody listening hears it, and the room shows its
            title, artist and artwork. Listeners can favorite it, add it to a playlist, or open
            your profile from it.
          </li>
        </Points>
        <p>
          If you do not want a track available for this, mark it unlisted or private. That limits
          who can play it and keeps it out of the queues and the automatic top-up. It is your
          decision to make per track, and you can change it whenever you like.
        </p>
      </Clause>

      <Clause id="not-recorded" title="4. Rooms are not recorded">
        <p>
          We do not keep broadcasts. There is no archive of a host's set, and no page anywhere on
          this service where a past broadcast can be played back.
        </p>
        <p>
          When a host streams from an encoder, the technology we use creates a recording as a
          side effect of making the stream watchable at all. We delete those, on a schedule, and
          they are never offered to anybody.
        </p>
        <p>
          What survives a broadcast is a list: what was played, when, and by whom. That is a
          record of the evening, not a copy of your music.
        </p>
      </Clause>

      <Clause id="elsewhere" title="5. Where your music can end up">
        <Points>
          <li>
            <strong className="text-ink">On other websites, if somebody embeds it.</strong> The
            service has an embeddable player. A blog or a social post can frame a track, and it
            plays from here, under your name, with a link back to you.
          </li>
          <li>
            <strong className="text-ink">In a link preview.</strong> When somebody shares a track,
            the preview card carries its title and artwork. That is the sharer's doing and the
            ordinary behavior of the web.
          </li>
          <li>
            <strong className="text-ink">In a host's own broadcast elsewhere.</strong> Hosts often
            stream to a video platform at the same time as running a room here. We do not send your
            recording to those services and have no arrangement with them, but a host broadcasting
            their own set can be heard playing it, the same way a radio show can.
          </li>
        </Points>
        <p>
          We do not put your music into advertising, on our own social accounts, or into any
          promotion off this service without asking you first.
        </p>
      </Clause>

      <Clause id="money" title="6. Money here does not buy any right in your music">
        <p>
          People can pay to move a submission up a host's queue, and can tip a host. Sponsorship
          cards appear in some parts of the site. None of that is a purchase of anything of yours.
        </p>
        <Points>
          <li>
            A boost buys a place in a queue, from the host. It buys nothing from you and nothing in
            your recording.
          </li>
          <li>
            A sponsorship shown near your track is not an endorsement by you, and we will not
            suggest that it is.
          </li>
          <li>
            We do not currently pay artists for plays here, and we do not pretend otherwise
            anywhere on this site. If that changes, it will change with your agreement and not by
            a quiet edit to a document.
          </li>
        </Points>
      </Clause>

      <Clause id="downloads" title="7. We do not hand out your records">
        <p>
          The service streams. There is no download button on a track, and we never offer the full
          recording as a file to anybody.
        </p>
        <p>
          There is one exception and it is worth reading. When somebody pastes a link to one of
          your public tracks into a chat app, we publish the first 30 seconds of it as a small
          video file so that it can play there without them leaving the conversation. That file
          sits at a public address, which means anybody who has the link can play it or save it.
          It is a preview, never the whole record, and it is built from your own artwork and your
          own audio with nothing added.
        </p>
        <p>
          You can turn this off in your settings, and clips already published come down when you
          do. Deleting a track removes its clip along with everything else, as clause 8 describes.
          One honest caveat: a chat app that already fetched a clip may keep showing its own copy
          for a while afterwards, the same way it keeps the artwork and the title. We can take ours
          down and we do, immediately. We cannot reach into theirs.
        </p>
        <p>
          We cannot stop somebody recording what comes out of their own speakers, and neither can
          anybody else. What we can promise is that the 30 seconds above is the only copy of your
          music we hand to anybody, and that we will not quietly extend it.
        </p>
      </Clause>

      <Clause id="taking-it-down" title="8. Taking it down ends the permission">
        <p>
          Delete a track and the license in clause 2 ends with it. We stop serving it immediately
          and remove it from our systems, including backups, within 30 days.
        </p>
        <p>
          Two things outlive a deletion, and both are records rather than copies: a room's history
          may still show that a track of that name was played on a particular evening, and anything
          somebody made outside this service was never ours to remove.
        </p>
        <p>
          Closing your account removes your catalog with it. You do not have to ask permission or
          give a reason.
        </p>
      </Clause>

      <Clause id="your-side" title="9. What we need from you">
        <p>
          The permission you give is only worth what you actually hold. You may only upload
          material you own or are licensed to distribute, and that means every layer of it: the
          recording, the composition, any sample, any interpolation, any vocal, any artwork.
        </p>
        <p>
          This matters to you as much as to us. A host who plays a track that turns out to be an
          uncleared sample is the one taking the complaint, and the artist whose work it actually
          was is owed better than a takedown notice.
        </p>
      </Clause>

      <Clause id="somebody-else" title="10. If somebody uploads your music">
        <p>
          Tell us and we will take it down. You do not need a lawyer and you do not need an
          account here to do it.
        </p>
        <p>
          Send a notice through <Link to="/dmca" className="text-accent hover:underline">our
          copyright claim form</Link>, or write to{' '}
          <a href={`mailto:${legal.contact.copyright}`} className="text-accent hover:underline">
            {legal.contact.copyright}
          </a>
          . Accounts that repeatedly infringe are removed; a strike counts for {legal.strikeMonths}{' '}
          months.
        </p>
      </Clause>

      <Clause id="the-terms" title="11. How this fits with the Terms">
        <p>
          This page is clause 3 of the{' '}
          <Link to="/terms#your-content" className="text-accent hover:underline">
            Terms of Service
          </Link>
          , written out in full and in plain words. It grants us nothing the terms do not already
          grant, and it takes nothing away from you.
        </p>
        <p>
          The terms are the agreement. If anything here ever conflicts with them, the terms govern,
          and we would rather you told us about the conflict than discovered it later:{' '}
          <a href={`mailto:${legal.contact.support}`} className="text-accent hover:underline">
            {legal.contact.support}
          </a>
          .
        </p>
        <p>
          If we ever change what uploading grants, we will say so before it takes effect, on this
          page and to the people it affects. A license in a document that grew while nobody was
          looking is the thing this page exists to be the opposite of.
        </p>
      </Clause>
    </LegalPage>
  );
}
